import { useState, useRef, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { ai, getToken } from '../api/client.js';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function SmartSearch({ onClose, onSelectFont }) {
  const { theme } = useTheme();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [fontsReady, setFontsReady] = useState({});
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Load fonts via FontFace API, trigger re-render when each is ready
  useEffect(() => {
    if (!results.length) return;
    setFontsReady({});

    results.forEach(async (f, idx) => {
      try {
        if (f.source === 'google-fonts') {
          // Load directly from Google's CDN (already allowed by font-src CSP)
          if (!f.regularUrl) {
            setFontsReady(prev => ({ ...prev, [idx]: true }));
            return;
          }
          const fontFace = new FontFace(f.family, `url(${f.regularUrl})`);
          const loaded = await fontFace.load();
          document.fonts.add(loaded);
        } else if (f.source === 'adobe-fonts') {
          // Typekit CSS is already injected globally; just wait for availability
          await document.fonts.load(`1em '${f.family}'`).catch(() => {});
        } else {
          // Uploaded font — fetch binary and load via blob URL
          if (!f.preview_file_id) return;
          const resp = await fetch(
            `${API_BASE}/fonts/${f.id}/serve/${f.preview_file_id}`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );
          if (!resp.ok) return;
          const blob = await resp.blob();
          const blobUrl = URL.createObjectURL(blob);
          const fontFace = new FontFace(f.family, `url(${blobUrl})`);
          const loaded = await fontFace.load();
          document.fonts.add(loaded);
        }
        setFontsReady(prev => ({ ...prev, [idx]: true }));
      } catch (_) {}
    });
  }, [results]);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const data = await ai.smartSearch(query.trim());
      setResults(data.fonts || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: 96,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 620, maxWidth: '92vw', maxHeight: '70vh',
          background: theme.bg,
          border: `2px solid ${theme.text}`,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div aria-hidden="true" style={{ height: 4, background: theme.accent }} />

        {/* Search Input */}
        <div style={{ padding: '18px 20px', borderBottom: `1px solid ${theme.border}` }}>
          <div style={{
            fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase',
            color: theme.textFaint, marginBottom: 10,
          }}>ai smart search</div>
          <div style={{ display: 'flex' }}>
            <input
              ref={inputRef}
              type="text"
              placeholder="describe what you're looking for — e.g. clean geometric sans for a tech brand"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              style={{
                flex: 1, height: 44, padding: '6px 14px',
                border: `1px solid ${theme.borderMedium}`, background: 'transparent',
                fontFamily: 'inherit', fontSize: 14, color: theme.text,
                outline: 'none', borderRadius: 0,
              }}
            />
            <button
              onClick={handleSearch}
              disabled={loading || !query.trim()}
              style={{
                height: 44, width: 56, marginLeft: -1,
                background: query.trim() ? theme.fill : theme.bgInput,
                border: 'none', cursor: query.trim() ? 'pointer' : 'default',
                color: query.trim() ? theme.fillFg : theme.textFaint, fontSize: 13,
              }}
            >{loading ? '…' : '▶'}</button>
          </div>
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 20px 16px' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: 32, color: theme.textMuted, fontSize: 13 }}>
              <div style={{
                width: 24, height: 24, border: `2px solid ${theme.borderMedium}`,
                borderTopColor: theme.accent, borderRadius: '50%',
                margin: '0 auto 12px',
                animation: 'spin 0.8s linear infinite',
              }} />
              analyzing your font library…
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {!loading && searched && results.length === 0 && (
            <div style={{ textAlign: 'center', padding: 32, color: theme.textFaint, fontSize: 13 }}>
              no matching fonts found. try a different description.
            </div>
          )}

          {!loading && results.map((f, idx) => (
            <button
              key={`${f.source}:${f.family}`}
              onClick={() => { if (f.id) onSelectFont(f.id); onClose(); }}
              style={{
                width: '100%', padding: '14px 0',
                background: 'none', border: 'none',
                borderBottom: `1px solid ${theme.borderLight}`,
                cursor: 'pointer', textAlign: 'left', display: 'block', fontFamily: 'inherit',
              }}
            >
              {/* Font preview in actual font face */}
              <div style={{
                fontFamily: `'${f.family}', serif`,
                fontSize: 28, lineHeight: 1.2,
                color: theme.text,
                overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                opacity: fontsReady[idx] ? 1 : 0.3,
                transition: 'opacity 0.3s',
              }}>
                The quick brown fox
              </div>

              {/* Metadata row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                <span style={{ fontFamily: "'Aeonik', Arial, Helvetica, sans-serif", fontWeight: 450, fontSize: 13, color: theme.text }}>{f.family}</span>
                <span style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: theme.textFaint }}>{f.category}</span>
                {f.source === 'google-fonts' && (
                  <span style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.textFaint, border: `1px solid ${theme.border}`, padding: '1px 6px' }}>google</span>
                )}
                {f.source === 'adobe-fonts' && (
                  <span style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.textFaint, border: `1px solid ${theme.border}`, padding: '1px 6px' }}>adobe</span>
                )}
                <span style={{ fontSize: 11, color: theme.accent }}>{f.reason}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Footer hint */}
        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${theme.border}`,
          fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: theme.textFaint,
          display: 'flex', justifyContent: 'space-between',
        }}>
          <span>powered by claude</span>
          <span onClick={onClose} style={{ cursor: 'pointer', color: theme.accent }}>close</span>
        </div>
      </div>
    </div>
  );
}
